<template>
  <div id="device-overview">
    <v-tabs v-model="currentTab" show-arrows>
      <v-tab v-for="tab in tabs" :key="tab.icon">
        <v-icon class="me-3">
          {{ tab.icon }}
        </v-icon>
        <span>
          {{ tab.title }}
        </span>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="currentTab" touchless>
      <v-tab-item>
        <v-card class="mb-4">
          <v-card-text>
            <v-row>
              <v-col>
                <h2 class="text-left text-xl font-weight-semibold label">Device Settings</h2>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="device_settings.tags"
                  label="Tags (comma-separated)"
                  outlined
                  dense
                  :disabled="!canConfig"
                >
                </v-text-field>
              </v-col>
            </v-row>
            <v-row v-if="canConfig">
              <v-col class="d-flex justify-end">
                <v-btn color="primary" @click="saveDeviceSettings">Save</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <v-tab-item v-if="canNotes">
        <v-card>
          <!-- Notes Filter fields start -->
          <v-row class="pt-4 pb-0 ma-0">
            <v-col cols="12" sm="4">
              <v-select
                v-model="sqPriority"
                :items="priorityItems"
                item-text="text"
                item-value="value"
                label="Priority"
                placeholder="Priority"
                outlined
                hide-details
                clearable
                dense
                class="user-search me-3"
                @change="val => addToSearchHistory(val, 'sqPriority')"
              >
              </v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="sqClaimant"
                label="Created By"
                outlined
                hide-details
                clearable
                persistent-clear
                dense
                class="user-search me-3"
                @change="val => addToSearchHistory(val, 'sqClaimant')"
              >
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-menu
                v-model="menu"
                :close-on-content-click="true"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="sqCreated"
                    label="Date"
                    readonly
                    v-bind="attrs"
                    outlined
                    hide-details
                    dense
                    clearable
                    class="user-search me-3"
                    v-on="on"
                    @change="val => addToSearchHistory(val, 'sqCreated')"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="sqCreated" no-title @input="menu = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <!-- Notes Filter fields end -->
          <!-- Notes Table start -->
          <v-data-table
            :headers="noteHeaders"
            :items="filteredNotes"
            :sort-by.sync="noteSortBy"
            :sort-desc.sync="noteSortDesc"
            :custom-sort="customSort"
            class="font-weight-semibold text--primary"
            :must-sort="true"
          >
            <template v-slot:item.id="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.id }}</span>
                  </div>
                </div>
              </router-link>
            </template>
            <template v-slot:item.title="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.title }}</span>
                  </div>
                </div>
              </router-link>
            </template>
            <template v-slot:item.priority="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.priority }}</span>
                  </div>
                </div>
              </router-link>
            </template>
            <template v-slot:item.status="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.status }}</span>
                  </div>
                </div>
              </router-link>
            </template>
            <template v-slot:item.claimant="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.claimant }}</span>
                  </div>
                </div>
              </router-link>
            </template>
            <template v-slot:item.convertCreatedDate="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <span class="font-weight-semibold text--primary" v-html="convertCreatedDate(item)"></span>
                  </div>
                </div>
              </router-link>
            </template>
          </v-data-table>
          <!-- Notes Table end -->

          <NotesForm :notes-data="notesData" />
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card class="mb-4">
          <v-card-text>
            <h2 class="text-left text-xl font-weight-semibold label mb-3">Bandwidth</h2>
            <v-row class="mt-1 mb-1" style="align-items: center">
              <v-col cols="5">
                <v-text-field
                  id="startDateTime"
                  v-model="startDate"
                  label="Start datetime"
                  outlined
                  hide-details
                  dense
                  type="date"
                />
              </v-col>
              <v-col cols="5">
                <v-text-field
                  id="endDateTime"
                  v-model="endDate"
                  label="End datetime"
                  outlined
                  hide-details
                  dense
                  type="date"
                />
              </v-col>
              <v-col cols="2">
                <v-btn color="primary" style="min-width: 100%" @click="fetchNetworkData">Go</v-btn>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-data-table
                  :headers="[
                    {
                      text: 'Date',
                      value: 'date',
                      sortable: true,
                    },
                    {
                      text: 'Upload',
                      value: 'upload',
                      sortable: true,
                    },
                    {
                      text: 'Download',
                      value: 'download',
                      sortable: true,
                    },
                    {
                      text: 'Total Usage',
                      value: 'total_usage',
                      sortable: true,
                    },
                  ]"
                  :items="networkUsageData"
                >
                  <template v-slot:item="{ item }">
                    <tr>
                      <td>{{ item.date }}</td>
                      <td>{{ item.upload }}</td>
                      <td>{{ item.download }}</td>
                      <td>{{ item.total_usage }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-text>
            <h2 class="text-left text-xl font-weight-semibold label mb-3">Device Metrics</h2>
            <DeviceMetricsChart
              :device-id="device.device_id"
              :annotations="metricsAnnotations"
              @range-changed="onMetricsRangeChanged"
            />
          </v-card-text>
        </v-card>

        <v-card>
          <v-card-text>
            <h2 class="text-left text-xl font-weight-semibold label mb-3">Trip Assignments</h2>
            <v-progress-circular v-if="tripAssignmentsLoading" color="primary" indeterminate></v-progress-circular>
            <v-data-table v-else :headers="tripAssignmentHeaders" :items="tripAssignments" :items-per-page="10" dense>
              <template v-slot:item.trip_id="{ item }">{{ item.trip_id || "null" }}</template>
              <template v-slot:item.action="{ item }">{{ item.action || "null" }}</template>
              <template v-slot:no-data> Click "Go" above to load trip assignments for the selected range. </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-card class="mt-4">
          <v-card-text>
            <h2 class="text-left text-xl font-weight-semibold label mb-3">Device Error Logs</h2>
            <v-progress-circular v-if="errorLogsLoading" color="primary" indeterminate></v-progress-circular>
            <v-data-table
              v-else
              :headers="errorLogHeaders"
              :items="errorLogs"
              :items-per-page="10"
              sort-by="date_time"
              sort-desc
              dense
            >
              <template v-slot:item.date_time="{ item }">
                <span class="text-no-wrap">{{ item.date_time }}</span>
              </template>
              <template v-slot:item.message="{ item }">
                <span class="text-pre-wrap">{{ item.message }}</span>
              </template>
              <template v-slot:no-data> Click "Go" above to load error logs for the selected range. </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-card class="mt-4">
          <v-card-text>
            <h2 class="text-left text-xl font-weight-semibold label mb-3">SSD Health</h2>
            <DeviceSsdHealth
              :records="ssdHealth"
              :loading="ssdHealthLoading"
              :range-start="metricsRange.startMs"
              :range-end="metricsRange.endMs"
            />
          </v-card-text>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-data-table :headers="historyHeaders" :items="locationHistoryData"></v-data-table>
              </v-col>
            </v-row>
            <v-row class="mb-2">
              <v-col cols="12" class="d-flex justify-start">
                <v-btn color="primary" @click="goToUpdateLocation">Update Location</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { mdiChartLine, mdiSwapHorizontal, mdiMessagePlus, mdiTextBoxMultipleOutline } from "@mdi/js"
import { mapState } from "vuex"
import byteSize from "byte-size"
import axios from "axios"

import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import DeviceMetricsChart from "./DeviceMetricsChart.vue"
import DeviceSsdHealth from "./DeviceSsdHealth.vue"
import NotesForm from "../../notes/components/NotesForm.vue"
import store from "@/store"
import urlHelpers from "@/mixins/urlHelpers"
import globalHelpers from "@/mixins/globalHelpers"
import { getConfigTagsText } from "@/utils/tags"

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: "DeviceOverview",

  components: {
    NotesForm,
    DeviceMetricsChart,
    DeviceSsdHealth,
  },

  mixins: [urlHelpers, globalHelpers],

  data() {
    return {
      currentTab: "",
      provisioning: [],
      is_loading: true,
      device_settings: {},
      sqCreated: "",
      sqClaimant: "",
      sqPriority: null,
      noteSortBy: "created",
      curNotes: [],
      networkUsage: [],
      networkUsageRequest: axios.CancelToken.source(),
      locationHistory: [],
      locationHistoryRequest: axios.CancelToken.source(),
      tripAssignments: [],
      tripAssignmentsLoading: false,
      tripAssignmentsRequest: axios.CancelToken.source(),
      tripAssignmentHeaders: [
        { text: "Action", value: "action", sortable: false },
        { text: "Trip ID", value: "trip_id", sortable: false },
        { text: "Date/Time", value: "date_time" },
      ],
      errorLogs: [],
      errorLogsLoading: false,
      errorLogsRequest: axios.CancelToken.source(),
      errorLogHeaders: [
        { text: "Service", value: "service" },
        { text: "Level", value: "level" },
        { text: "Message", value: "message", sortable: false },
        { text: "Date/Time", value: "date_time" },
      ],
      ssdHealth: [],
      ssdHealthLoading: false,
      ssdHealthRequest: axios.CancelToken.source(),
      metricsRange: { start: null, end: null, startMs: null, endMs: null },
      historyHeaders: [
        { text: "Date", value: "location_date", sortable: true },
        { text: "Sender", value: "sender_organisation", sortable: true },
        { text: "Receiver", value: "receiving_organisation", sortable: true },
        { text: "Vehicle ID", value: "vehicle_id", sortable: true },
        { text: "Status", value: "location_status", sortable: false },
        { text: "User", value: "user", sortable: false },
      ],
      organisations: ["Radiola", "Quickstart", "Somar", "GWRC"],
      menu: false,
      // Notes Table Headers
      noteHeaders: [
        { text: "Note ID", value: "id" },
        { text: "Title", value: "title" },
        { text: "Priority", value: "priority" },
        { text: "Status", value: "status" },
        { text: "Created By", value: "claimant" },
        { text: "Created", value: "convertCreatedDate" },
      ],
      priorityItems: [
        {
          text: "Low",
          value: "Low",
        },
        {
          text: "Medium",
          value: "Medium",
        },
        {
          text: "High",
          value: "High",
        },
        {
          text: "Urgent",
          value: "Urgent",
        },
      ],
      notesData: {
        device_id: null,
        case_id: null,
        claimant: this.$auth.user.name,
      },
      startDate: dayjs.tz(new Date(), "Pacific/Auckland").subtract(1, "week").format("YYYY-MM-DD"),
      endDate: dayjs.tz(new Date(), "Pacific/Auckland").subtract(1, "day").format("YYYY-MM-DD"),
      noteSortDesc: true, // true for descending order, false for ascending order
    }
  },
  computed: {
    ...mapState({
      device: state => state.selected_device,
      notes: state => state.gtfs.notes,
    }),
    tabs() {
      const tabs = []

      tabs.push({ title: "Configuration", icon: mdiMessagePlus })

      if (this.canNotes) {
        tabs.push({ title: "Notes", icon: mdiTextBoxMultipleOutline })
      }

      tabs.push({ title: "Metrics", icon: mdiChartLine })
      tabs.push({ title: "Transfers", icon: mdiSwapHorizontal })

      return tabs
    },
    canConfig() {
      return this.$auth.hasScope("devices")
    },
    canNotes() {
      return this.$auth.hasScope("notes")
    },
    updateLocationRoute() {
      return {
        name: "next-stop-devices",
        query: {
          tab: "transfers",
        },
      }
    },
    // Notes Filtering
    filteredNotes() {
      // Ensure curNotes is an array before calling filter
      if (!Array.isArray(this.curNotes)) return []

      return this.curNotes.filter(note => {
        let include = true
        if (include && this.sqCreated !== "" && this.sqCreated !== null) {
          // eslint-disable-next-line no-undef
          const date = dayjs(this.formatDate(note.created, "en-NZ"), "D/MM/YYYY, h:mm:ss a")
          include = date.format("YYYY-MM-DD") === this.sqCreated
        }
        if (include && this.sqClaimant !== "" && this.sqClaimant !== null) {
          include = note.claimant.toLowerCase().includes(this.sqClaimant.toLowerCase())
        }
        if (include && this.sqPriority !== "" && this.sqPriority !== null) {
          include = note.priority === this.sqPriority
        }

        return include
      })
    },
    convertCreatedDate() {
      // eslint-disable-next-line func-names
      return function (item) {
        return item.created ? this.formatDate(item.created, "en-NZ") : "Unknown"
      }
    },
    networkUsageData() {
      if (!this.networkUsage.length) {
        return []
      }

      return this.networkUsage.map(item => ({
        device_id: item.device_id,
        vehicle_id: item.vehicle_id,
        upload: byteSize(item.upload).toString(),
        upload_bytes: item.upload,
        download: byteSize(item.download).toString(),
        download_bytes: item.download,
        total_usage: byteSize(item.upload + item.download).toString(),
        total_bytes: item.upload + item.download,
        date: dayjs.tz(item.date, "Pacific/Auckland").format("YYYY-MM-DD"),
      }))
    },
    locationHistoryData() {
      if (!this.locationHistory.length) {
        return []
      }

      return this.locationHistory.map(item => ({
        ...item,
        location_date: item.created ? this.formatDate(item.created, "en-NZ") : "Unknown",
      }))
    },
    metricsAnnotations() {
      const annotations = []

      this.tripAssignments.forEach(item => {
        const ts = dayjs.tz(item.date_time, "Pacific/Auckland").valueOf()
        if (!Number.isFinite(ts)) {
          return
        }
        const isConfirmed = item.action === "confirmed"
        annotations.push({
          timestamp: ts,
          label: `${isConfirmed ? "Confirmed" : "Sent"} ${item.trip_id || "null"}`,
          color: isConfirmed ? "#2E7D32" : "#81C784",
          type: isConfirmed ? "trip_assignment_confirmed" : "trip_assignment_sent",
          typeLabel: isConfirmed ? "Trip confirmed" : "Trip sent",
        })
      })

      this.errorLogs.forEach(item => {
        const ts = dayjs.tz(item.date_time, "Pacific/Auckland").valueOf()
        if (!Number.isFinite(ts)) {
          return
        }
        annotations.push({
          timestamp: ts,
          label: `Error ${item.service || "unknown"}`,
          color: "#C62828",
          type: "error_log",
          typeLabel: "Error logs",
        })
      })

      return annotations
    },
  },
  async created() {
    this.device_settings = JSON.parse(JSON.stringify(this.device.config || {}))

    // Tags are read back from tag_details but still saved as a comma separated
    // string, so tag_details itself stays out of what we post
    delete this.device_settings.tag_details
    this.device_settings.tags = getConfigTagsText(this.device.config)

    this.notesData.device_id = this.device.device_id
    this.notesData.case_id = this.device.config.case_id

    try {
      this.curNotes = await store.dispatch("getNotes", {
        device_id: this.device.device_id,
        case_id: this.device.config.case_id,
      })
    } catch (error) {
      console.error("Failed to load notes:", error)
      this.curNotes = []
    }

    await this.fetchLocationHistoryData()
  },
  methods: {
    // The transfers screen takes its selection from the store, the same way the
    // device list's Transfer button seeds it, so preselect this device before
    // handing over
    goToUpdateLocation() {
      const caseId = this.device?.config?.case_id

      store.commit("SET_SELECTED_DEVICE_HARDWARE_IDS", caseId ? [caseId] : [])
      this.$router.push(this.updateLocationRoute)
    },
    saveDeviceSettings() {
      if (this.canConfig) {
        store.dispatch("saveDeviceConfig", this.device_settings)
      }
    },
    async fetchNetworkData() {
      this.loading = true

      const startDate = dayjs.tz(this.startDate, "Pacific/Auckland").format("YYYY-MM-DD 00:00:00")
      const endDate = dayjs.tz(this.endDate, "Pacific/Auckland").format("YYYY-MM-DD 23:59:59")

      try {
        const [networkUsage] = await Promise.all([this.fetchNetworkUsage(startDate, endDate, this.device.device_id)])

        this.networkUsage = networkUsage
      } catch (error) {
        console.error(error)
      }

      this.loading = false
    },
    fetchNetworkUsage(startDate, endDate, deviceId) {
      this.networkUsageRequest.cancel("New fetch requested")
      this.networkUsageRequest = axios.CancelToken.source()

      return store.dispatch("getNetworkUsageByDevice", {
        start_datetime: startDate,
        end_datetime: endDate,
        device_id: deviceId,
        cancelToken: this.networkUsageRequest.token,
      })
    },
    onMetricsRangeChanged({ start, end, startMs, endMs }) {
      this.metricsRange = { start, end, startMs, endMs }
      this.fetchTripAssignments()
      this.fetchErrorLogs()
      this.fetchSsdHealth()
    },
    async fetchSsdHealth() {
      if (!this.device?.device_id || !this.metricsRange.start || !this.metricsRange.end) {
        this.ssdHealth = []

        return
      }

      this.ssdHealthRequest.cancel("New fetch requested")
      this.ssdHealthRequest = axios.CancelToken.source()
      this.ssdHealthLoading = true

      try {
        const data = await store.dispatch("getDeviceEventsByVehicleId", {
          device_id: this.device.device_id,
          event_type: "ssd_health",
          start_datetime: this.metricsRange.start,
          end_datetime: this.metricsRange.end,
          cancelToken: this.ssdHealthRequest.token,
        })

        if (data && data.error) {
          throw new Error(data.error)
        }

        this.ssdHealth = (data || []).map(i => ({
          type: i.event_type,
          ...i.event_data,
        }))
      } catch (e) {
        if (!axios.isCancel(e)) {
          console.error("Error fetching SSD health:", e)
          this.ssdHealth = []
        }
      } finally {
        this.ssdHealthLoading = false
      }
    },
    async fetchErrorLogs() {
      if (!this.device?.device_id || !this.metricsRange.start || !this.metricsRange.end) {
        this.errorLogs = []

        return
      }

      this.errorLogsRequest.cancel("New fetch requested")
      this.errorLogsRequest = axios.CancelToken.source()
      this.errorLogsLoading = true

      try {
        const data = await store.dispatch("getDeviceEventsByVehicleId", {
          device_id: this.device.device_id,
          event_type: "error_logs",
          start_datetime: this.metricsRange.start,
          end_datetime: this.metricsRange.end,
          cancelToken: this.errorLogsRequest.token,
        })

        if (data && data.error) {
          throw new Error(data.error)
        }

        this.errorLogs = (data || []).map(i => ({
          type: i.event_type,
          ...i.event_data,
        }))
      } catch (e) {
        if (!axios.isCancel(e)) {
          console.error("Error fetching device error logs:", e)
          this.errorLogs = []
        }
      } finally {
        this.errorLogsLoading = false
      }
    },
    async fetchTripAssignments() {
      if (!this.device?.device_id || !this.metricsRange.start || !this.metricsRange.end) {
        this.tripAssignments = []

        return
      }

      this.tripAssignmentsRequest.cancel("New fetch requested")
      this.tripAssignmentsRequest = axios.CancelToken.source()
      this.tripAssignmentsLoading = true

      try {
        const data = await store.dispatch("getDeviceEventsByVehicleId", {
          device_id: this.device.device_id,
          event_type: "trip_assignments",
          start_datetime: this.metricsRange.start,
          end_datetime: this.metricsRange.end,
          cancelToken: this.tripAssignmentsRequest.token,
        })

        if (data && data.error) {
          throw new Error(data.error)
        }

        this.tripAssignments = (data || []).map(i => ({
          type: i.event_type,
          ...i.event_data,
        }))
      } catch (e) {
        if (!axios.isCancel(e)) {
          console.error("Error fetching trip assignments:", e)
          this.tripAssignments = []
        }
      } finally {
        this.tripAssignmentsLoading = false
      }
    },
    async fetchLocationHistoryData() {
      const hardwareId = this.device?.config?.case_id
      if (!hardwareId) {
        this.locationHistory = []

        return
      }

      this.locationHistoryRequest.cancel("New history fetch requested")
      this.locationHistoryRequest = axios.CancelToken.source()

      try {
        const history = await store.dispatch("getDeviceLocationHistory", {
          hardware_id: hardwareId,
          cancelToken: this.locationHistoryRequest.token,
        })

        this.locationHistory = Array.isArray(history) ? history : []
      } catch (error) {
        console.error(error)
      }
    },

    /**
     * Overrides default sorting by table headers.
     */
    customSort(items, sortBy, sortDesc) {
      const sortByField = sortBy[0] ?? ""
      const sortDescending = sortDesc[0] ?? false

      items.sort((a, b) => {
        let sortA = null
        let sortB = null

        // Handle sorting based on clicked header column
        switch (sortByField) {
          case "convertCreatedDate": {
            // We don't want to use convertCreatedDate as it can return "Unknown"
            sortA = new Date(a?.created)
            sortB = new Date(b?.created)

            break
          }
          case "priority": {
            // Order by priority not alphabetically
            const priorityOrder = {
              urgent: 1,
              high: 2,
              medium: 3,
              low: 4,
            }

            sortA = priorityOrder[a.priority?.toLowerCase()] ?? 5
            sortB = priorityOrder[b.priority?.toLowerCase()] ?? 5

            break
          }
          default: {
            // Default sorting
            sortA = a[sortBy]
            sortB = b[sortBy]

            break
          }
        }

        if (sortA < sortB) {
          return -1
        }

        if (sortA > sortB) {
          return 1
        }

        return 0
      })

      // Reverse order if header clicked again
      if (sortDescending) {
        items.reverse()
      }

      return items
    },
  },
}
</script>
